import { ThemeProvider as SCThemeProvider } from "styled-components";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AxiosProvider, WfAuth0ProviderFromViteConfig, WfAuthnProvider, useAxios } from "@wayflyer/auth-sdk";
import { BlackBox, type DatadogInitConfig } from "@wayflyer/blackbox-fe";
import { AppShellController, FlyUIProvider, NavigationProvider } from "@wayflyer/flyui";
import {
  AppErrorModalProvider,
  BankConnectConsentProvider,
  CompanyInactiveModal,
  ConnectorProvider,
  ErrorComponent,
  getCustomerErrorPresetMap,
  getDevelopmentEnvironmentTopBanners,
  getErrorVariant,
  getFlyUIEnvironment,
  InviteModalRenderer,
  InviteProvider,
  useFindViewOwner,
  useErrorPageProps,
  usePostHogPageView,
} from "shared-ui";
import { bindInsightsIntl } from "@wayflyer/customer-insights";
import { queryClient, AnalyticsProvider } from "shared-utils";
import { BaseSchemas } from "@wayflyer/api-types";
import type { BankConnectScreenProps } from "@wayflyer/customer-funding-application";
import { bankConnectScreenPropsOptions } from "@wayflyer/customer-funding-application";
import { memo, Suspense, useCallback } from "react";
import { createIntl, createIntlCache, IntlProvider, useIntl } from "react-intl";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { AppConfigAuthRedirect } from "../../authn/app-config-auth-redirect";
import { getAnalyticsAttributes } from "../analytics/get-analytics-attributes";
import { usePostHogIdentify } from "../analytics/posthog";
import { collectPrivacyPolicyConsent } from "../consent";
import { ConsentContextProvider } from "../context-providers/ConsentContext";
import { convertNumDaysToDateRange, getNumDaysSinceDate } from "../utils/date-helpers";
import { Fallback } from "../../fallback";

import { IntelligenceRuntimeGate } from "./intelligence-runtime-gate";
import { useIntercomLoaded } from "./intercom";
import { InviteCookieBootstrap } from "./invite-cookie-bootstrap";
import { getCustomerLanguage, getLocale } from "./locale";
import { useNavigationItems } from "./navigation/use-navigation-items";

import "@wayflyer/flyui/styles.css";
import "@wayflyer/flyui-dataviz/styles.css";

const messagesCache = {};

const getDatadogUserContext = (appConfig: BaseSchemas.CustomerAppConfig): DatadogInitConfig["user"] | undefined => {
  const analyticsAttributes = getAnalyticsAttributes(appConfig);
  const { userName, userEmail, customerName } = appConfig;
  if (!("userId" in analyticsAttributes) || analyticsAttributes.userId == null) {
    return undefined;
  }
  const lastLoginInDays = analyticsAttributes.lastLogin && getNumDaysSinceDate(analyticsAttributes.lastLogin);
  const lastLoginDateRange = lastLoginInDays && convertNumDaysToDateRange(lastLoginInDays);
  const deviceResolution = `${window.screen.width}x${window.screen.height}`;
  const isValidSessionRedirect = !!sessionStorage.getItem("validSessionRedirect");
  return {
    id: String(analyticsAttributes.userId),
    name: userName,
    isStaff: analyticsAttributes.isStaff,
    lastLogin: analyticsAttributes.lastLogin,
    lastLoginInDays,
    lastLoginDateRange,
    createdAt: analyticsAttributes.createdAt,
    customerName,
    deviceResolution,
    validSessionRedirect: isValidSessionRedirect,
    email: userEmail,
    isInternalUser: userEmail?.endsWith("@wayflyer.com"),
    ...analyticsAttributes,
  };
};

function getMessages(locale) {
  if (messagesCache[locale]) {
    return messagesCache[locale];
  }
  throw loadMessages(locale);
}

async function loadMessages(locale) {
  let messages;
  if (locale.startsWith("es")) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/es.json");
  } else if (locale.startsWith("de")) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/de.json");
  } else {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/en.json");
  }

  messagesCache[locale] = messages.default;
  return messages.default;
}

const intlCache = createIntlCache();

const AsyncIntlProvider = ({ children }) => {
  const locale = getLocale();
  const messages = getMessages(locale);

  // The insights section formats copy in module-scope code that cannot reach the React context, so it reads an
  // `intl` from a module instead. Point that at the same locale and messages before any screen renders.
  bindInsightsIntl(createIntl({ locale, messages, defaultLocale: "en", onError: () => {} }, intlCache));

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale="en" onError={() => {}}>
      {children}
    </IntlProvider>
  );
};

const NavigationWithInviteContext = memo(
  ({
    appConfig,
    bankConnectPropsData,
    children,
  }: {
    appConfig: BaseSchemas.CustomerAppConfig;
    bankConnectPropsData: BankConnectScreenProps;
    children: React.ReactNode;
  }) => {
    const navigationProps = useNavigationItems({ appConfig });
    const axios = useAxios();

    const consentHandler = useCallback(async () => {
      await collectPrivacyPolicyConsent(axios, appConfig.customerDomain);
    }, [axios, appConfig.customerDomain]);

    const topBanners = [
      ...(navigationProps.topBanners ?? []),
      ...getDevelopmentEnvironmentTopBanners({ id: "customer-development-environment-warning" }),
    ];

    return (
      <NavigationProvider {...navigationProps} topBanners={topBanners}>
        <InviteModalRenderer />
        <CompanyInactiveModal isOpen={!!appConfig.customerInactive} />
        <ConnectorProvider>
          <BankConnectConsentProvider
            consentNeeded={Boolean(bankConnectPropsData?.bankConnectProps.consentNeeded)}
            consentHandler={consentHandler}
          >
            <ConsentContextProvider consentHandler={consentHandler}>{children}</ConsentContextProvider>
          </BankConnectConsentProvider>
        </ConnectorProvider>
      </NavigationProvider>
    );
  }
);

NavigationWithInviteContext.displayName = "NavigationWithInviteContext";

const RootContainer = ({ children }: { children: React.ReactNode }) => {
  const { data: appConfig } = useCustomerAppConfig();

  return (
    <AppConfigAuthRedirect authError={appConfig.authError}>
      <ConfiguredRootContainer appConfig={appConfig}>{children}</ConfiguredRootContainer>
    </AppConfigAuthRedirect>
  );
};

const ConfiguredRootContainer = ({
  appConfig,
  children,
}: {
  appConfig: BaseSchemas.CustomerAppConfig;
  children: React.ReactNode;
}) => {
  const analyticsAttributes = getAnalyticsAttributes(appConfig);
  useIntercomLoaded();
  useFindViewOwner();
  const intl = useIntl();
  const customerLanguage = getCustomerLanguage(intl.locale);
  const { data: bankConnectPropsData } = useQuery({
    ...bankConnectScreenPropsOptions(appConfig.customerDomain),
    enabled: !!appConfig.customerDomain,
  });
  usePostHogIdentify({ ...appConfig, analyticsAttributes }, customerLanguage);
  usePostHogPageView();

  return (
    <BlackBox
      FallbackComponent={ErrorFallback}
      resolveVariant={getErrorVariant}
      user={getDatadogUserContext(appConfig)}
    >
      <AnalyticsProvider value={analyticsAttributes}>
        <InviteProvider customerSlug={appConfig.customerDomain}>
          <FlyUIProvider
            LegacySCProvider={SCThemeProvider}
            intl={intl}
            theme="wayflyer"
            defaultLinkBehavior="clientSideNav"
            environment={getFlyUIEnvironment({ isStaff: analyticsAttributes.isStaff ?? false })}
          >
            <AppErrorModalProvider>
              <NavigationWithInviteContext appConfig={appConfig} bankConnectPropsData={bankConnectPropsData}>
                <AppShellController>
                  <IntelligenceRuntimeGate>{children}</IntelligenceRuntimeGate>
                </AppShellController>
              </NavigationWithInviteContext>
            </AppErrorModalProvider>
          </FlyUIProvider>
        </InviteProvider>
      </AnalyticsProvider>
    </BlackBox>
  );
};

const ErrorFallback = ({ error, resetErrorBoundary, variant }) => {
  const intl = useIntl();
  const presets = getCustomerErrorPresetMap({ intl });
  const props = useErrorPageProps(error, {
    ...(presets[variant] ?? presets.unknown),
    secondaryAction: {
      content: intl.formatMessage({ id: "error.goBack", defaultMessage: "Go back" }),
      onAction: resetErrorBoundary,
    },
  });
  return (
    <ErrorPage>
      <ErrorComponent {...props} />
    </ErrorPage>
  );
};

export function RootComponent({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Fallback />}>
      <QueryClientProvider client={queryClient}>
        <AxiosProvider>
          <AppTree>{children}</AppTree>
        </AxiosProvider>
      </QueryClientProvider>
    </Suspense>
  );
}

function AppTree({ children }: { children: React.ReactNode }) {
  return (
    <AsyncIntlProvider>
      <WfAuth0ProviderFromViteConfig>
        <WfAuthnProvider>
          <InviteCookieBootstrap>
            <RootContainer>{children}</RootContainer>
          </InviteCookieBootstrap>
        </WfAuthnProvider>
      </WfAuth0ProviderFromViteConfig>
    </AsyncIntlProvider>
  );
}

// We want to keep the rendering of our error components as simple as possible to avoid
// the Error components throwing errors themselves. Avoid wrapping/mounting with unnecessary providers.
export function ErrorPage({ children }: { children: React.ReactNode }) {
  return (
    <AxiosProvider>
      <QueryClientProvider client={queryClient}>
        <AsyncIntlProvider>
          <FlyUIProvider LegacySCProvider={SCThemeProvider} theme="wayflyer">
            {children}
          </FlyUIProvider>
        </AsyncIntlProvider>
      </QueryClientProvider>
    </AxiosProvider>
  );
}
